import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { nativeUpdater, type Updater } from '@/lib/apkUpdater';
import { fetchLatest, isNewer, type LatestApp } from '@/lib/appUpdate';
import { APP_VERSION } from '@/lib/env';
import { isNative } from '@/lib/platform';
import { AppUpdateContext, type AppUpdate, type UpdateState } from './appUpdateContext';
import { UpdateDialog } from './UpdateDialog';

export interface AppUpdateDeps {
  /** Só o app Android se atualiza sozinho; no navegador o site já é sempre o mais novo. */
  native: boolean;
  updater: Updater;
  fetchLatest: () => Promise<LatestApp | null>;
  currentVersion: string;
  /** Abre o APK no navegador do aparelho (plano B quando o download dentro do app falha). */
  openExternal: (url: string) => void;
  /** Chama `listener` toda vez que o app volta para a frente; devolve como parar de ouvir. */
  onResume: (listener: () => void) => () => void;
}

const defaultDeps: AppUpdateDeps = {
  native: isNative(),
  updater: nativeUpdater,
  fetchLatest: () => fetchLatest(),
  currentVersion: APP_VERSION,
  openExternal: (url) => {
    window.location.href = url;
  },
  onResume: (listener) => {
    // Carregado só quando o app Android pede (o mesmo módulo que app/lifecycle.ts já carrega assim).
    const handle = import('@capacitor/app').then(({ App }) =>
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          listener();
        }
      }),
    );
    return () => void handle.then((registered) => registered.remove());
  },
};

/** Intervalo mínimo entre as checagens automáticas feitas ao voltar para o app. */
const RECHECK_MS = 6 * 60 * 60 * 1000;

/**
 * Atualização do app Android por dentro dele: ao abrir (e ao voltar depois de horas), pergunta ao site qual é a versão mais nova
 * e, se há uma, sugere atualizar. Atualizar baixa o APK (conferido pelo SHA-256) e abre o instalador do Android.
 */
export function AppUpdateProvider({
  deps = defaultDeps,
  children,
}: {
  deps?: AppUpdateDeps;
  children: ReactNode;
}) {
  const [state, setState] = useState<UpdateState>({ kind: 'idle' });
  const [promptOpen, setPromptOpen] = useState(false);
  const dismissed = useRef<string | null>(null);
  const lastCheck = useRef(0);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  /** O que fazer com a resposta do site. `manual` (o botão do perfil) sempre abre o aviso e sempre responde algo. */
  const apply = useCallback(
    (latest: LatestApp | null, manual: boolean) => {
      if (latest === null) {
        setState((current) => (manual ? { kind: 'unknown' } : current));
        return;
      }

      if (!isNewer(latest, deps.currentVersion)) {
        setState({ kind: 'upToDate' });
        return;
      }

      setState({ kind: 'available', latest });
      if (manual || dismissed.current !== latest.version) {
        setPromptOpen(true);
      }
    },
    [deps],
  );

  const check = useCallback(
    async ({ manual = false }: { manual?: boolean } = {}) => {
      if (!deps.native) {
        return;
      }

      lastCheck.current = Date.now();
      if (manual) {
        setState({ kind: 'checking' });
      }

      apply(await deps.fetchLatest(), manual);
    },
    [deps, apply],
  );

  const download = useCallback(
    async (latest: LatestApp) => {
      setState({ kind: 'downloading', latest, progress: 0 });
      try {
        await deps.updater.downloadAndInstall({ url: latest.apkUrl, sha256: latest.sha256 }, (progress) =>
          setState({ kind: 'downloading', latest, progress }),
        );
        setState({ kind: 'installing', latest });
      } catch (error) {
        const message =
          error instanceof Error && error.message ? error.message : 'Não foi possível baixar a atualização.';
        setState({ kind: 'error', latest, message });
      }
    },
    [deps],
  );

  const start = useCallback(() => {
    const current = stateRef.current;
    if (current.kind !== 'available' && current.kind !== 'needsPermission' && current.kind !== 'error') {
      return;
    }

    const latest = current.latest;
    void (async () => {
      const allowed = await deps.updater.canInstall().catch(() => false);
      if (!allowed) {
        setState({ kind: 'needsPermission', latest });
        return;
      }

      await download(latest);
    })();
  }, [deps, download]);

  const dismiss = useCallback(() => {
    const current = stateRef.current;
    if ('latest' in current) {
      dismissed.current = current.latest.version;
    }

    setPromptOpen(false);
  }, []);

  // Ao abrir o app.
  useEffect(() => {
    if (!deps.native) {
      return undefined;
    }

    let cancelled = false;
    lastCheck.current = Date.now();
    void deps.fetchLatest().then((latest) => {
      if (!cancelled) {
        apply(latest, false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [deps, apply]);

  // Ao voltar para o app: se a pessoa tinha ido liberar a instalação, segue sozinho; senão, confere de novo depois de umas horas.
  useEffect(() => {
    if (!deps.native) {
      return undefined;
    }

    return deps.onResume(() => {
      const current = stateRef.current;
      if (current.kind === 'needsPermission') {
        start();
      } else if (Date.now() - lastCheck.current > RECHECK_MS && !('latest' in current)) {
        void check();
      }
    });
  }, [deps, check, start]);

  const value = useMemo<AppUpdate>(
    () => ({
      enabled: deps.native,
      currentVersion: deps.currentVersion,
      state,
      check,
      start,
      dismiss,
      openInBrowser: () => {
        if ('latest' in state) {
          deps.openExternal(state.latest.apkUrl);
        }
      },
      openInstallSettings: () => void deps.updater.openInstallSettings().catch(() => undefined),
    }),
    [deps, state, check, start, dismiss],
  );

  return (
    <AppUpdateContext.Provider value={value}>
      {children}
      {deps.native && promptOpen ? <UpdateDialog /> : null}
    </AppUpdateContext.Provider>
  );
}
